import { computed, inject, nextTick, ref, watch } from 'vue';
import {
  Builds_BuildsWhereInput,
  Builds_InitiatorType,
  Builds_Result,
  Builds_SortOrder,
  Builds_Summary,
  useLicensePageLazyQuery,
  useWorkloadsPageBuildsQuery,
  useWorkloadsPageBuildsLazyQuery,
  useWorkloadsPageSummaryLazyQuery,
  useWorkloadsPageMetadataLazyQuery,
} from '@/graphql/codegen/generated/graphql';
import { IKWorkloadsPageData, IKWorkloadViewPageData } from '@/views/workloads/workloads.injectionKeys';
import { OsType } from '@/modules/platformAuthentication/platformAuthentication.const';
import moment from 'moment';
import { app } from '@/bootloader';
import { useLazyQueryWithCoordinator } from '@/services/graphql.utils';
import { Constants } from '@/const/globals';
import { IKCoordinatorsService } from '@/modules/CoordinatorsService/CoordinatorService.InjectionKeys';
import { useOsService } from '@/services/OsService/OsService';
import { useWorkloadsTableConstants } from '@/views/workloads/workloads.const';
import { IKApplicationPersistentData } from '@/services/persistentDataProvider/persistentDataProvider.types';
import { IKWorkloadsPersistentData } from '@/views/workloads/workloads-table.types';
import { TableColumns } from '@/views/agents/components/agents-table/types/agents-table.types';
import _ from 'lodash';

export type WorkloadsPagePersistentDataType = {
  tableEditor: {
    windows: {
      columnsOrder: TableColumns<OsType.Windows>;
      columnsFilter: Partial<Record<string, boolean>>;
    };
    linux: {
      columnsOrder: TableColumns<OsType.Linux>;
      columnsFilter: Partial<Record<string, boolean>>;
    };
    mac: {
      columnsOrder: TableColumns<OsType.Mac>;
      columnsFilter: Partial<Record<string, boolean>>;
    };
  };
  filter: {
    windows: Builds_BuildsWhereInput;
    linux: Builds_BuildsWhereInput;
  };
  customFilter: string;
};

export const useWorkloadsPersistentDataProvider = () => {
  const appPersistentData = inject(IKApplicationPersistentData);
  const osService = useOsService();
  const workloadsPagePersistentData = ref<WorkloadsPagePersistentDataType | any>({
    filter: {
      windows: {},
      linux: {},
    },
    tableEditor: {
      windows: {},
      linux: {},
      mac: {},
    },
  });

  const workloadsPageSection = 'WorkloadsPage';

  watch(
    () => appPersistentData?.GQL?.networkStatus?.value,
    () => {
      const result = appPersistentData?.getPersistentData(workloadsPageSection) as WorkloadsPagePersistentDataType;
      if (result && typeof result === 'object' && Object.keys(result).length > 0) {
        workloadsPagePersistentData.value = result as WorkloadsPagePersistentDataType;
      }
    },
    {
      immediate: true,
    },
  );

  const getFilterData = computed(() => workloadsPagePersistentData.value?.filter?.[osService?.osType.value || OsType.Windows]);

  const setFilterData = (value: any) => {
    workloadsPagePersistentData.value.filter[osService?.osType.value || OsType.Windows] = value;
    appPersistentData?.setPersistentData(workloadsPageSection, workloadsPagePersistentData.value);
  };

  const getTableEditorData = computed(() => workloadsPagePersistentData.value?.tableEditor?.[osService?.osType.value || OsType.Windows]);

  const setTableEditorData = (value: any) => {
    workloadsPagePersistentData.value.tableEditor[osService?.osType.value || OsType.Windows] = value;
    appPersistentData?.setPersistentData(workloadsPageSection, workloadsPagePersistentData.value);
  };

  const setCustomFilter = (value: string = '') => {
    workloadsPagePersistentData.value.customFilter = value;
    appPersistentData?.setPersistentData(workloadsPageSection, workloadsPagePersistentData.value);
  };

  const getCustomFilter = computed(() => workloadsPagePersistentData.value?.customFilter);

  const result = {
    getFilterData,
    setFilterData,
    getTableEditorData,
    setTableEditorData,
    setCustomFilter,
    getCustomFilter,
  };

  app.provide(IKWorkloadsPersistentData, result);
  return result;
};

export const useWorkloadsFilterDataProvider = () => {
  const workloadsPersistentData = inject(IKWorkloadsPersistentData);
  const osService = useOsService();
  const workloadsConstants = useWorkloadsTableConstants();

  const handleTimePresetFilter = (from: number, to: number) => {
    const daysDiff = moment().subtract(from).diff(moment().subtract(to), 'days');

    const result = {
      startTime: moment(from),
      endTime: moment(to),
    };

    switch (daysDiff) {
      case 0: // Today
        result.startTime = moment().subtract(0, 'days').startOf('day');
        result.endTime = moment().endOf('day');
        break;
      case 6: // Last 7 days
        result.startTime = moment().subtract(6, 'days').startOf('day');
        result.endTime = moment().endOf('day');
        break;
      case 13: // Last 14 days
        result.startTime = moment().subtract(13, 'days').startOf('day');
        result.endTime = moment().endOf('day');
        break;
      default:
        break;
    }
    return {
      startTime: result.startTime.unix() * 1000,
      endTime: result.endTime.unix() * 1000,
    };
  };

  const defaultPageSize = 50;
  const defaultFilterValue = {
    overlappingTimeframes: false,
    buildResult: [],
    buildGroup: [],
    buildType: [],
    initiatorType: [],
    searchFilter: null,
    durationFrom: null,
    durationTo: null,
    sort: {
      field: osService?.isWindows.value
        ? workloadsConstants.WORKLOADS_COLUMNS_TO_SORT_MAP[OsType.Windows][workloadsConstants.COLUMNS_NAMES[OsType.Windows].startTime]
        : workloadsConstants.WORKLOADS_COLUMNS_TO_SORT_MAP[OsType.Linux][workloadsConstants.COLUMNS_NAMES[OsType.Linux].title],
      order: Builds_SortOrder.Descending,
    },
    paging: {
      pageNumber: 1,
      pageSize: defaultPageSize,
    },
    timeframes: [],
  } as Builds_BuildsWhereInput;

  const workloadsPayload = ref<Builds_BuildsWhereInput>(_.cloneDeep(defaultFilterValue));

  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
  workloadsPayload.value.timeZone = timeZone;

  const workloadsQueryFilter = {
    get: computed(() => workloadsPayload.value),
    getFilterDefaultValue: () => ({
      ...defaultFilterValue,
      timeZone,
    }),
    set(newValue: typeof workloadsPayload.value, isInternal = false) {
      workloadsPayload.value = newValue;
      if (isInternal) {
        return;
      }
      workloadsPersistentData?.setFilterData(workloadsPayload.value);
    },
    setPageNumber(newValue: typeof workloadsPayload.value.paging.pageNumber) {
      this.set({
        ...workloadsPayload.value,
        paging: {
          ...workloadsPayload.value.paging,
          pageNumber: newValue,
        },
      });
    },
    setDefaultTimeFrame(isInternal = false) {
      // Default timeframe to get data is last 14 days
      const now = moment();
      const to = now.endOf('day').unix() * 1000;
      const from = now.subtract(13, 'days').startOf('day').unix() * 1000;
      this.setTimeFrame([{ from, to }], undefined, isInternal);
    },
    getTimeFrame: computed(() => ({
      startTime: Math.min(...(workloadsPayload.value.timeframes?.map(({ startTime }) => startTime) || [0])),
      endTime: Math.max(0, ...(workloadsPayload.value.timeframes?.map(({ endTime }) => endTime) || [])),
    })),
    setPageSize(newValue: typeof workloadsPayload.value.paging.pageSize, isInternal = false) {
      this.set(
        {
          ...workloadsPayload.value,
          paging: {
            pageSize: newValue,
            pageNumber: 1,
          },
        },
        isInternal,
      );
    },
    setBuildResult(newValue: typeof workloadsPayload.value.buildResult, isInternal = false) {
      this.set(
        {
          ...workloadsPayload.value,
          buildResult: newValue,
        },
        isInternal,
      );
    },
    setInitiatorType(newValue: typeof workloadsPayload.value.initiatorType, isInternal = false) {
      this.set(
        {
          ...workloadsPayload.value,
          initiatorType: newValue,
        },
        isInternal,
      );
    },
    setBuildGroup(newValue: typeof workloadsPayload.value.buildGroup, isInternal = false) {
      this.set(
        {
          ...workloadsPayload.value,
          buildGroup: newValue,
        },
        isInternal,
      );
    },
    setSorting(newValue: typeof workloadsPayload.value.sort, isInternal = false) {
      this.set(
        {
          ...workloadsPayload.value,
          sort: newValue,
        },
        isInternal,
      );
    },
    setBuildType(newValue: typeof workloadsPayload.value.buildType, isInternal = false) {
      this.set(
        {
          ...workloadsPayload.value,
          buildType: newValue,
        },
        isInternal,
      );
    },
    setSearchFilter(newValue: typeof workloadsPayload.value.searchFilter, isInternal = false) {
      this.set(
        {
          ...workloadsPayload.value,
          searchFilter: newValue,
        },
        isInternal,
      );
    },
    setDuration({ from, to }: { from: typeof workloadsPayload.value.durationFrom; to: typeof workloadsPayload.value.durationTo }, isInternal = false) {
      this.set(
        {
          ...workloadsPayload.value,
          durationFrom: from,
          durationTo: to,
        },
        isInternal,
      );
    },
    setTimeFrame(timeframes: { from: ReturnType<Date['getTime']>; to: ReturnType<Date['getTime']> }[], overlappingTimeframes = false, isInternal = false) {
      this.set(
        {
          ...workloadsPayload.value,
          timeframes: timeframes.map(({ from, to }) => ({
            startTime: from,
            endTime: to,
          })),
          paging: {
            ...workloadsPayload.value.paging,
            pageNumber: 1,
          },
          overlappingTimeframes,
        },
        isInternal,
      );
    },

    areFiltersInDefaultState: computed(
      () =>
        workloadsPayload?.value?.buildGroup?.length === 0 &&
        workloadsPayload?.value?.durationFrom === null &&
        workloadsPayload?.value?.durationTo === null &&
        workloadsPayload?.value?.buildResult?.length === 0 &&
        workloadsPayload?.value?.initiatorType?.length === 0 &&
        workloadsPayload?.value?.buildType?.length === 0,
    ),

    clear(isInternal = false) {
      this.setDefaultTimeFrame(true);
      this.setBuildGroup([], true);
      this.setBuildResult([], true);
      this.setBuildType([], true);
      this.setSearchFilter(null, true);
      this.setDuration({ from: null, to: null }, true);
      this.setInitiatorType(null, true);
      this.setSorting(
        {
          field: osService?.isWindows.value
            ? workloadsConstants.WORKLOADS_COLUMNS_TO_SORT_MAP[OsType.Windows][workloadsConstants.COLUMNS_NAMES[OsType.Windows].title]
            : workloadsConstants.WORKLOADS_COLUMNS_TO_SORT_MAP[OsType.Linux][workloadsConstants.COLUMNS_NAMES[OsType.Linux].title],
          order: Builds_SortOrder.Descending,
        } as any,
        true,
      );
      workloadsPayload.value.paging.pageNumber = 1;
      // last set should send update to the server (this is why false)
      this.setPageSize(defaultPageSize, isInternal);
    },
  };

  const isTimePreset = (startTime: number, endTime: number) => {
    const today = moment().startOf('day');
    const startOf7Days = moment().subtract(6, 'days').startOf('day');
    const startOf14Days = moment().subtract(13, 'days').startOf('day');

    const start = moment(startTime).startOf('day');
    const end = moment(endTime).startOf('day');

    return (
      (start.isSame(today, 'day') && end.isSame(today, 'day')) || (start.isSame(startOf7Days, 'day') && end.isSame(today, 'day')) || (start.isSame(startOf14Days, 'day') && end.isSame(today, 'day'))
    );
  };

  const setWorkloadsPersistentData = (workloadsFilter: WorkloadsPagePersistentDataType | any) => {
    if (workloadsFilter.overlappingTimeframes === false && (workloadsFilter?.timeframes as any[])?.every(({ startTime, endTime }) => isTimePreset(startTime, endTime))) {
      workloadsQueryFilter.set(
        {
          ...workloadsFilter,
          timeframes: (workloadsFilter?.timeframes as any[])?.map(({ startTime, endTime }) => handleTimePresetFilter(startTime, endTime)) || [],
        },
        true,
      );
    } else {
      workloadsQueryFilter.set(workloadsFilter, true);
    }
  };

  watch(
    () => workloadsPersistentData?.getFilterData.value,
    workloadsFilter => {
      // get persistent data for workloads page happens in App.ts so here is must be already available
      if (!workloadsFilter) return;
      if (typeof workloadsFilter === 'object' && Object.keys(workloadsFilter).length === 0) {
        workloadsQueryFilter.setDefaultTimeFrame(true);
      } else setWorkloadsPersistentData(workloadsFilter);
    },
    { immediate: true },
  );

  return { workloadsQueryFilter };
};

export const useWorkloadsDataProvider = () => {
  const { workloadsQueryFilter } = useWorkloadsFilterDataProvider();
  const applicationPersistentData = inject(IKApplicationPersistentData);
  const license = useLazyQueryWithCoordinator(useLicensePageLazyQuery);
  const coordinatorService = inject(IKCoordinatorsService);

  const osService = useOsService();

  const GQL_useWorkloadsPageBuildsQuery = useWorkloadsPageBuildsLazyQuery(
    () => ({
      filter: {
        ...workloadsQueryFilter.get.value,
        coordinatorId: null,
      },
      coordinatorId: coordinatorService?.selectedCoordinator.value,
    }),
    { fetchPolicy: 'no-cache' },
  );

  const GQL_useWorkloadsPageSummaryQuery = useWorkloadsPageSummaryLazyQuery(
    () => ({
      filterSummary: {
        timeframes: workloadsQueryFilter.get.value.timeframes || [],
        overlappingTimeframes: workloadsQueryFilter.get.value.overlappingTimeframes || false,
        coordinatorId: null,
      },
    }),
    { fetchPolicy: 'no-cache' },
  );

  const GQL_useWorkloadsPageMetadataQuery = useWorkloadsPageMetadataLazyQuery(
    () => ({
      filterMetadata: {
        timeframes: workloadsQueryFilter.get.value.timeframes || [],
        overlappingTimeframes: workloadsQueryFilter.get.value.overlappingTimeframes || false,
        coordinatorId: null,
      },
    }),
    { fetchPolicy: 'no-cache' },
  );

  nextTick(() => {
    watch(
      // app load call
      () => applicationPersistentData?.GQL?.networkStatus?.value,
      () => {
        GQL_useWorkloadsPageBuildsQuery.load();
        GQL_useWorkloadsPageMetadataQuery.load();
        GQL_useWorkloadsPageSummaryQuery.load();
      },
      { immediate: true },
    );
  }).then(() => {});

  const summary = computed(() => GQL_useWorkloadsPageSummaryQuery.result.value?.ibManagerEntities?.buildsSummary as Builds_Summary);
  const workloads = computed(() => GQL_useWorkloadsPageBuildsQuery.result.value?.ibManagerEntities?.builds as Builds_Result);
  const workloadsBuildGroups = computed(() => (GQL_useWorkloadsPageMetadataQuery.result.value?.ibManagerEntities?.buildsMetadata?.buildGroups as string[]) || []);
  const workloadsDurationMaxNumber = computed(() => (GQL_useWorkloadsPageMetadataQuery.result.value?.ibManagerEntities?.buildsMetadata?.maxDuration as number) || 0);
  const oldestBuildStartTime = computed(() => (GQL_useWorkloadsPageMetadataQuery.result.value?.ibManagerEntities?.buildsMetadata?.oldestBuildStartTime as number) || 0);
  const isFilterOutOfLicenseRange = computed(() => {
    const showEnterpriseObservability =
      license.result.value?.ibManagerEntities?.license?.windows?.features?.productFeatures?.includes(Constants.LICENSE_FEATURES.ENTERPRISE_OBSERVABILITY) ||
      license.result.value?.ibManagerEntities?.license?.windows?.tierName?.toLowerCase().includes(Constants.TIERS.ENTERPRISE.toLowerCase());
    const isFilterBeyond14Days = moment(workloadsQueryFilter.getTimeFrame.value?.startTime).isBefore(moment().subtract(14, 'days'));
    return osService?.isWindows.value ? !showEnterpriseObservability && isFilterBeyond14Days : false; // not relevant for Linux
  });
  const containsBuildCacheData = computed(() => (GQL_useWorkloadsPageMetadataQuery.result.value?.ibManagerEntities?.buildsMetadata?.hasBuildCache as boolean) || false);
  const hasBuildCacheInLicense = computed(() => !!GQL_useWorkloadsPageBuildsQuery.result.value?.ibManagerEntities?.licenseMetadata?.hasBuildCache);
  const initiatorTypes = computed(() => (GQL_useWorkloadsPageMetadataQuery.result.value?.ibManagerEntities?.buildsMetadata?.initiatorTypes as [Builds_InitiatorType]) || []);
  const workloadsBuilds = computed(() => workloads.value?.builds || []);
  const containsCloudRelevantData = computed(() => (workloadsBuilds.value as [])?.some((workload: any) => workload.cloudTasks > 0));

  const ciLicense = computed(() => {
    const { totalFloatingCIInitiators, totalFixedCIInitiators } = license.result.value?.ibManagerEntities?.license?.windows?.summary || {};
    return {
      totalFixedCIInitiators: totalFixedCIInitiators || 0,
      totalFloatingCIInitiators: totalFloatingCIInitiators || 0,
    };
  });

  const result = {
    GQL: GQL_useWorkloadsPageBuildsQuery,
    summary,
    workloads,
    isFilterOutOfLicenseRange,
    workloadsBuildGroups,
    workloadsDurationMaxNumber,
    oldestBuildStartTime,
    containsBuildCacheData,
    hasBuildCacheInLicense,
    initiatorTypes,
    containsCloudRelevantData,
    ciLicense,
    filter: workloadsQueryFilter,
  };

  app.provide(IKWorkloadsPageData, result);

  return result;
};

export const useWorkloadViewDataProvider = (buildId: string) => {
  const coordinatorId = null;
  const GQL_useWorkloadsPageQuery = useWorkloadsPageBuildsQuery(
    () => ({
      filter: {
        buildId,
        paging: {
          pageNumber: 1,
          pageSize: 1,
        },
        timeframes: [],
        coordinatorId,
      },
    }),
    { fetchPolicy: 'no-cache' },
  );

  const workload = computed(() => GQL_useWorkloadsPageQuery.result.value?.ibManagerEntities?.builds?.builds?.[0]);
  const result = {
    GQL: GQL_useWorkloadsPageQuery,
    workload,
  };
  app.provide(IKWorkloadViewPageData, result);
  return result;
};
export const useWorkloadViewData = () => inject(IKWorkloadViewPageData);
